// import { Slider } from '../ui/slider';
import { Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import { useCallback, useRef, useState } from 'react';
import type { AuthUser } from '@/features/auth/types';
import { deletePictureAuthUser, updatePictureAuthUser } from '@/features/auth/authThunks';
import { useAppDispatch } from '@/app/hooks';
import { useTranslation } from 'react-i18next';

export default function UpdateProfilPicture({
  user,
  onUploadSuccess,
}: {
  user: AuthUser;
  onUploadSuccess?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('user');
  // const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(user.pictureUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  console.log('isUploaded', isUploaded);
  console.log('isUploading', isUploading);
  console.log('selectedFile', selectedFile);

  const validateFile = useCallback(
    (file: File): boolean => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert(t('picture.invalidFormat'));
        return false;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(t('picture.tooLarge'));
        return false;
      }

      return true;
    },
    [t],
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!validateFile(file)) {
        return;
      }

      setSelectedFile(file);
      setIsUploaded(false);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [validateFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);
    try {
      const updatedUser = await dispatch(updatePictureAuthUser(selectedFile)).unwrap();
      if (updatedUser.pictureUrl) {
        setPreview(updatedUser.pictureUrl);
      }
      setSelectedFile(null);
      setIsUploaded(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onUploadSuccess?.();
    } catch (error) {
      console.error("Erreur lors de l'upload de la photo:", error);
      alert(t('picture.uploadError'));
    } finally {
      setIsUploading(false);
    }
  }, [dispatch, selectedFile, onUploadSuccess, t]);

  const handleDelete = useCallback(async () => {
    if (!user.pictureUrl && !preview) {
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(deletePictureAuthUser()).unwrap();
      setPreview(null);
      setSelectedFile(null);
      setIsUploaded(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      alert(t('picture.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, preview, user.pictureUrl, t]);

  const displayImage = preview || user.pictureUrl;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs text-secondary">{t('picture.formatHint')}</p>
        <p className="text-xs text-secondary">{t('picture.dimensionsHint')}</p>
        <p className="text-xs text-secondary">{t('picture.weightHint')}</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {displayImage ? (
        <div className="space-y-6">
          <div className="w-full h-80 rounded-lg overflow-hidden relative bg-gray-200">
            <img
              src={displayImage}
              alt={t('picture.title')}
              className="w-full h-full object-contain transition-transform duration-200 ease-out"
              style={{
                // transform: `scale(${zoom})`,
                transformOrigin: 'center center',
              }}
            />
            {/* <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                maskImage:
                  'radial-gradient(circle closest-side at center, transparent 100%, black 100%)',
                WebkitMaskImage:
                  'radial-gradient(circle closest-side at center, transparent 100%, black 100%)',
              }}
            /> */}
          </div>
          {/* <div className="w-full flex flex-col items-center justify-center space-y-2">
            <p className="text-xs text-secondary">Zoom</p>
            <Slider
              min={1}
              max={3}
              step={0.1}
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0] ?? 1)}
            />
          </div> */}
        </div>
      ) : (
        <div
          className={`p-8 rounded-lg border-4 border-dashed flex items-center justify-center flex-col cursor-pointer transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-card hover:border-primary/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className="flex items-center flex-col gap-6">
            <div className="bg-[#EAF0FF] size-10 rounded-lg flex items-center justify-center ">
              <Plus className="size-6" />
            </div>

            <div className="space-y-2 flex flex-col items-center justify-center">
              <p className="text-base text-secondary">{t('picture.dropHere')}</p>
              <p className="text-xs text-secondary">{t('picture.or')}</p>
              <p className="text-base">{t('picture.chooseFile')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          className="h-12"
          disabled={!displayImage || isDeleting}
          onClick={handleDelete}
        >
          {isDeleting ? t('picture.deleting') : t('picture.remove')}
        </Button>
        <Button
          className="h-12"
          disabled={!selectedFile || isUploading || isUploaded}
          onClick={handleSave}
        >
          {isUploading ? t('actions.saving') : t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
